import React, { useMemo, useState } from 'react';
import { PreDemoFlag } from './DemoUI';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Search, Download, Ban, ExternalLink } from 'lucide-react';
import { fmtDate, STATUS_META, OUTCOME_META } from './demoConstants';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const FbChips = ({ r }) => (
  <span className="flex flex-wrap gap-1">
    <span className={`px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold border ${r.trainer_feedback_missing ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>T {r.trainer_feedback_missing ? '⏳' : '✓'}</span>
    <span className={`px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold border ${r.counsellor_feedback_missing ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>C {r.counsellor_feedback_missing ? '⏳' : '✓'}</span>
  </span>
);

/**
 * One drilldown pop-up for every number in the demo reports.
 * `variant='revenue'` rows carry invoice data, `variant='demos'` rows are plain demos.
 */
export default function DemoDrilldownModal({ open, onClose, title, subtitle, rows = [], variant = 'revenue', onOpenDemo }) {
  const [q, setQ] = useState('');
  const [outcome, setOutcome] = useState('all');
  const [fb, setFb] = useState('all');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (needle && ![r.student_name, r.demo_code, r.trainer_name, r.counsellor_name, r.enrollment_no, r.invoice_number, r.course_name]
        .some((v) => String(v || '').toLowerCase().includes(needle))) return false;
      const res = r.outcome_result || (variant === 'revenue' ? 'admission' : null);
      if (outcome !== 'all' && res !== outcome) return false;
      const missing = r.trainer_feedback_missing || r.counsellor_feedback_missing;
      if (fb === 'missing' && !missing) return false;
      if (fb === 'complete' && missing) return false;
      return true;
    });
  }, [rows, q, outcome, fb, variant]);

  const total = filtered.reduce((s, r) => s + Number(r.invoice_amount || 0), 0);
  const totalEx = filtered.reduce((s, r) => s + Number(r.amount_ex_gst || 0), 0);
  const totalPending = filtered.reduce((s, r) => s + Number(r.balance || 0), 0);

  const exportCsv = () => {
    const head = variant === 'revenue'
      ? ['Demo', 'Demo Date', 'Student', 'Enrolment No', 'Admission Date', 'Invoice No', 'Amount excl GST', 'Amount incl GST', 'Credit Notes excl GST', 'Net excl GST', 'Pending', 'Trainer', 'Counsellor', 'Trainer FB', 'Counsellor FB']
      : ['Demo', 'Demo Date', 'Student', 'Course', 'Trainer', 'Counsellor', 'Status', 'Outcome', 'Trainer FB', 'Counsellor FB'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const body = filtered.map((r) => (variant === 'revenue'
      ? [r.demo_code, r.demo_date, r.student_name, r.enrollment_no, r.admission_date, r.invoice_number, r.amount_ex_gst, r.invoice_amount, r.cn_ex_gst, r.net_ex_gst, r.balance, r.trainer_name, r.counsellor_name, r.trainer_feedback_missing ? 'MISSING' : 'Submitted', r.counsellor_feedback_missing ? 'MISSING' : 'Submitted']
      : [r.demo_code, r.demo_date, r.student_name, r.course_name, r.trainer_name, r.counsellor_name, r.status, r.outcome_result, r.trainer_feedback_missing ? 'MISSING' : 'Submitted', r.counsellor_feedback_missing ? 'MISSING' : 'Submitted']
    ).map(esc).join(','));
    const blob = new Blob([[head.map(esc).join(','), ...body].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(title || 'drilldown').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose?.(); setQ(''); setOutcome('all'); setFb('all'); } }}>
      <DialogContent className="max-w-6xl max-h-[88vh] overflow-hidden flex flex-col bg-white" data-testid="drilldown-modal">
        <DialogHeader>
          <DialogTitle className="text-base" data-testid="drilldown-title">{title}</DialogTitle>
          <DialogDescription className="text-[11.5px] text-slate-500">{subtitle}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Student, demo, invoice…" className="h-9 w-56 pl-8 bg-white text-[12.5px]" data-testid="drilldown-search" />
          </div>
          <Select value={outcome} onValueChange={setOutcome}>
            <SelectTrigger className="h-9 w-40 bg-white text-[12.5px]" data-testid="drilldown-outcome"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All outcomes</SelectItem>
              <SelectItem value="admission">Admission</SelectItem>
              <SelectItem value="no_admission">No admission</SelectItem>
              <SelectItem value="follow_up">Follow-up</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fb} onValueChange={setFb}>
            <SelectTrigger className="h-9 w-44 bg-white text-[12.5px]" data-testid="drilldown-feedback"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Any feedback state</SelectItem>
              <SelectItem value="missing">Feedback missing</SelectItem>
              <SelectItem value="complete">Feedback complete</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[11.5px] text-slate-500 ml-1" data-testid="drilldown-count">
            {filtered.length} of {rows.length} row(s){variant === 'revenue' ? ` · ${inr(totalEx)} excl. GST (${inr(total)} incl.)${totalPending > 0.5 ? ` · ${inr(totalPending)} pending` : ''}` : ''}
          </span>
          <Button variant="outline" size="sm" className="h-9 ml-auto" onClick={exportCsv} disabled={!filtered.length} data-testid="drilldown-export">
            <Download className="w-3.5 h-3.5 mr-1.5" />CSV
          </Button>
        </div>

        <div className="flex-1 overflow-auto mt-1 rounded-lg border border-slate-200">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                <th className="px-3 py-2.5">Demo</th>
                <th className="px-3 py-2.5">Student</th>
                {variant === 'revenue' ? <>
                  <th className="px-3 py-2.5">Admission</th>
                  <th className="px-3 py-2.5">Invoice</th>
                  <th className="px-3 py-2.5 text-right">Amount excl. GST</th>
                </> : <>
                  <th className="px-3 py-2.5">Course</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Outcome</th>
                </>}
                <th className="px-3 py-2.5">Trainer</th>
                <th className="px-3 py-2.5">Counsellor</th>
                <th className="px-3 py-2.5">Feedback</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className={`border-b border-slate-100 last:border-0 hover:bg-violet-50 transition-colors ${r.void ? 'opacity-60' : ''}`} data-testid={`drilldown-row-${r.id}`}>
                  <td className="px-3 py-2.5">
                    <span className={`font-mono text-[11px] text-slate-700 ${r.void ? 'line-through' : ''}`}>{r.demo_code}</span>
                    <span className="block text-[10px] text-slate-400">{fmtDate(r.demo_date)}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="text-slate-800">{r.student_name || '—'}</span>
                    {r.enrollment_no ? <span className="block font-mono text-[10px] text-slate-400">#{r.enrollment_no}</span> : null}
                  </td>
                  {variant === 'revenue' ? <>
                    <td className="px-3 py-2.5 text-slate-600">{r.admission_date ? fmtDate(r.admission_date) : <span className="text-amber-700">—</span>}</td>
                    <td className="px-3 py-2.5">
                      {r.invoice_number ? <span className="font-mono text-[10.5px] text-slate-700">{r.invoice_number}</span> : !r.earlier_invoice && !r.invoice_credited_to && <span className="text-[10.5px] text-amber-700">not raised</span>}
                      <PreDemoFlag r={r} />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="tabular-nums font-semibold text-slate-900">{inr(r.amount_ex_gst)}</span>
                      {r.invoice_id ? <span className="block tabular-nums text-[9.5px] text-slate-400">{inr(r.invoice_amount)} incl. GST</span> : null}
                      {r.cn_ex_gst > 0 && <span className="block tabular-nums text-[10px] text-rose-700">CN -{inr(r.cn_ex_gst)}</span>}
                      {r.balance > 0.5 && <span className="block tabular-nums text-[10px] text-amber-700">{inr(r.balance)} pending</span>}
                    </td>
                  </> : <>
                    <td className="px-3 py-2.5 text-slate-700">{r.course_name}</td>
                    <td className="px-3 py-2.5">
                      {r.void
                        ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold bg-slate-200 text-slate-600 border border-slate-300"><Ban className="w-2.5 h-2.5" />VOID</span>
                        : <span className={STATUS_META[r.status]?.cls}>{STATUS_META[r.status]?.label || r.status}</span>}
                    </td>
                    <td className="px-3 py-2.5">{r.outcome_result ? <span className={OUTCOME_META[r.outcome_result]?.cls}>{OUTCOME_META[r.outcome_result]?.label}</span> : <span className="text-slate-300">—</span>}</td>
                  </>}
                  <td className="px-3 py-2.5 text-slate-700">{r.trainer_name}</td>
                  <td className="px-3 py-2.5 text-slate-700">{r.counsellor_name}</td>
                  <td className="px-3 py-2.5"><FbChips r={r} /></td>
                  <td className="px-3 py-2.5 text-right">
                    {onOpenDemo && (
                      <button onClick={() => onOpenDemo(r.id)} className="text-violet-600 hover:text-violet-800" title="Open the demo" data-testid={`drilldown-open-${r.id}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-[12px] text-slate-400" data-testid="drilldown-empty">Nothing matches these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
